import { useEffect, useMemo, useState } from 'react';
import {
  Accordion, Badge, Button, Checkbox, Group, Loader, Modal, Paper, Select,
  Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconGripVertical, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HttpClient from '../helpers/HttpClient';
import { TeamDraft, TeamDraftEntry } from '../types/team-draft.type';
import { Person } from '../types/person.type';

const U_CATEGORIES = ['U17', 'U15', 'U14', 'U13', 'U12', 'U11', 'U10', 'U9', 'U8', 'U7', 'U6'];

const SPÄTGEBOREN_QUOTA: Record<string, number> = {
  U17: 3, U15: 3, U14: 2, U13: 2, U12: 2, U11: 2, U10: 2, U9: 2, U8: 2, U7: 2, U6: 2,
};

const U_TAG_PATTERN = /^U\d+$/;

const draftClient = new HttpClient<TeamDraft>('/team-draft');
const personClient = new HttpClient<Person>('/person');

function formatDate(d?: Date | string): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT');
}

function isSecondHalf(birthDate?: Date | string): boolean {
  if (!birthDate) return false;
  return new Date(birthDate).getMonth() >= 6; // July = index 6
}

function generateTeamDraftPdf(draft: TeamDraft, entriesByCategory: Record<string, TeamDraftEntry[]>) {
  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.getHeight();
  const title = draft.name ? `${draft.season} – ${draft.name}` : draft.season;

  doc.setFontSize(14);
  doc.text(title, 14, 16);
  doc.setFontSize(9);
  doc.text(`Saison: ${draft.season}`, 14, 22);

  let y = 30;
  U_CATEGORIES.forEach((cat) => {
    const entries = entriesByCategory[cat] ?? [];
    if (entries.length === 0) return;

    if (y > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }

    const sorted = [...entries].sort((a, b) =>
      `${a.person.lastName} ${a.person.firstName}`.localeCompare(`${b.person.lastName} ${b.person.firstName}`)
    );

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(cat, 14, y);
    doc.setFont('helvetica', 'normal');

    autoTable(doc, {
      startY: y + 3,
      head: [['Name']],
      body: sorted.map((e) => [`${e.person.lastName} ${e.person.firstName}`]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [60, 60, 60] },
    });

    y = (doc as any).lastAutoTable.finalY + 12;
  });

  doc.save(`Team-Entwurf_${draft.season}${draft.name ? '_' + draft.name : ''}.pdf`);
}

function TeamDraftDetail() {
  const { id } = useParams<{ id: string }>();
  const draftId = Number(id);
  const navigate = useNavigate();

  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const [addCategory, setAddCategory] = useState<string>('U17');
  const [addPersonId, setAddPersonId] = useState<string | null>(null);

  // Drag and drop state
  const [draggedEntryId, setDraggedEntryId] = useState<number | null>(null);
  const [dragOverCat, setDragOverCat] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmEntry, setDeleteConfirmEntry] = useState<TeamDraftEntry | null>(null);

  const load = async () => {
    try {
      const res = await draftClient.getById(draftId);
      setDraft(res.data);
    } catch {
      notifications.show({ color: 'red', message: 'Entwurf konnte nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    personClient.getAll().then(setPersons).catch(() => {
      notifications.show({ color: 'orange', message: 'Spielerliste konnte nicht geladen werden.' });
    });
  }, [draftId]);

  const entriesByCategory = useMemo(() => {
    const map: Record<string, TeamDraftEntry[]> = {};
    U_CATEGORIES.forEach(c => (map[c] = []));
    (draft?.entries ?? []).forEach(e => {
      if (!map[e.uCategory]) map[e.uCategory] = [];
      map[e.uCategory].push(e);
    });
    return map;
  }, [draft]);

  const stats = useMemo(() => {
    const total = draft?.entries?.length ?? 0;
    const perCat: Record<string, { total: number; spätgeboren: number }> = {};
    U_CATEGORIES.forEach(c => (perCat[c] = { total: 0, spätgeboren: 0 }));
    (draft?.entries ?? []).forEach(e => {
      const cat = perCat[e.uCategory] ?? (perCat[e.uCategory] = { total: 0, spätgeboren: 0 });
      cat.total++;
      if (e.isSpätgeboren) cat.spätgeboren++;
    });
    return { total, perCat };
  }, [draft]);

  const draftedPersonIds = useMemo(
    () => new Set((draft?.entries ?? []).map(e => e.person?.id)),
    [draft],
  );

  const availablePersonOptions = useMemo(
    () =>
      persons
        .filter(p => !draftedPersonIds.has(p.id))
        .map(p => ({
          value: String(p.id),
          label: `${p.lastName} ${p.firstName}`,
        })),
    [persons, draftedPersonIds],
  );

  const isFinalized = draft?.status === 'finalized';

  // ── API handlers ────────────────────────────────────────────────────────────

  const handleFinalize = async () => {
    if (!window.confirm('Entwurf abschließen? Alle U-Tags der Spieler werden aktualisiert.')) return;
    setActing(true);
    try {
      await draftClient.postAction(`${draftId}/finalize`);
      await load();
      notifications.show({ color: 'green', message: 'Entwurf wurde abgeschlossen und Tags vergeben.' });
    } catch {
      notifications.show({ color: 'red', message: 'Abschließen fehlgeschlagen.' });
    } finally {
      setActing(false);
    }
  };

  const handleUpdateEntry = async (entryId: number, data: Partial<TeamDraftEntry>) => {
    try {
      const entryHttpClient = new HttpClient<TeamDraftEntry>(`/team-draft/${draftId}/entry`);
      await entryHttpClient.patch(entryId, data as any);
      setDraft(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          entries: (prev.entries ?? []).map(e =>
            e.id === entryId ? { ...e, ...data } : e,
          ),
        };
      });
    } catch {
      notifications.show({ color: 'red', message: 'Änderung fehlgeschlagen.' });
    }
  };

  const handleRemoveEntry = async (entryId: number) => {
    try {
      const entryHttpClient = new HttpClient<TeamDraftEntry>(`/team-draft/${draftId}/entry`);
      await entryHttpClient.delete(entryId);
      setDraft(prev => {
        if (!prev) return prev;
        return { ...prev, entries: (prev.entries ?? []).filter(e => e.id !== entryId) };
      });
    } catch {
      notifications.show({ color: 'red', message: 'Entfernen fehlgeschlagen.' });
    }
  };

  const handleAddEntry = async () => {
    if (!addPersonId) return;
    try {
      await draftClient.postAction(`${draftId}/entry`, { personId: Number(addPersonId), uCategory: addCategory });
      setAddPersonId(null);
      await load();
    } catch {
      notifications.show({ color: 'red', message: 'Hinzufügen fehlgeschlagen.' });
    }
  };

  const handleGenerate = async () => {
    if (draft?.entries?.length && !window.confirm('Bestehende Zuordnungen werden überschrieben. Fortfahren?')) return;
    setActing(true);
    try {
      await draftClient.postAction(`${draftId}/generate`);
      await load();
      notifications.show({ color: 'green', message: 'Spieler wurden generiert.' });
    } catch {
      notifications.show({ color: 'red', message: 'Generieren fehlgeschlagen.' });
    } finally {
      setActing(false);
    }
  };

  // ── Drag and drop handlers ───────────────────────────────────────────────────

  const handleDragStart = (entryId: number) => setDraggedEntryId(entryId);
  const handleDragEnd = () => { setDraggedEntryId(null); setDragOverCat(null); };

  const handleDragOver = (e: React.DragEvent, cat: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCat(cat);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear when leaving the drop zone entirely (not its children)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverCat(null);
    }
  };

  const handleDrop = async (targetCat: string) => {
    setDragOverCat(null);
    if (!draggedEntryId) return;
    const entry = draft?.entries?.find(e => e.id === draggedEntryId);
    if (!entry || entry.uCategory === targetCat) { setDraggedEntryId(null); return; }
    setDraggedEntryId(null);
    await handleUpdateEntry(entry.id, { uCategory: targetCat });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return <Loader />;
  if (!draft) return <Text>Entwurf nicht gefunden.</Text>;

  return (
    <Stack gap="md">

      {/* Delete confirmation modal */}
      <Modal
        opened={!!deleteConfirmEntry}
        onClose={() => setDeleteConfirmEntry(null)}
        title="Spieler aus Entwurf entfernen"
        size="sm"
        withinPortal={false}
      >
        <Stack gap="md">
          <Text>
            <strong>
              {deleteConfirmEntry?.person?.lastName} {deleteConfirmEntry?.person?.firstName}
            </strong>{' '}
            wirklich aus dem Entwurf entfernen?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteConfirmEntry(null)}>Abbrechen</Button>
            <Button
              color="red"
              onClick={() => {
                if (deleteConfirmEntry) handleRemoveEntry(deleteConfirmEntry.id);
                setDeleteConfirmEntry(null);
              }}
            >
              Entfernen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" size="xs" onClick={() => navigate('/drafts')}>← Zurück</Button>
          <Title order={2}>
            {draft.season}{draft.name ? ` – ${draft.name}` : ''}
          </Title>
          <Badge color={isFinalized ? 'green' : 'blue'} size="lg">
            {isFinalized ? 'Abgeschlossen' : 'Entwurf'}
          </Badge>
        </Group>
        <Group gap="sm">
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={() => generateTeamDraftPdf(draft, entriesByCategory)}
          >
            PDF herunterladen
          </Button>
          {!isFinalized && (
            <>
              <Button variant="default" loading={acting} onClick={handleGenerate}>
                Generieren
              </Button>
              <Button color="green" loading={acting} onClick={handleFinalize}>
                Abschließen
              </Button>
            </>
          )}
        </Group>
      </Group>

      {/* Statistics */}
      <Paper withBorder p="sm">
        <Group gap="xs" wrap="wrap">
          <Text fw={600}>Gesamt: {stats.total}</Text>
          {U_CATEGORIES.filter(c => stats.perCat[c]?.total > 0).map(c => {
            const { total, spätgeboren } = stats.perCat[c];
            const quota = SPÄTGEBOREN_QUOTA[c];
            const sgOver = spätgeboren > quota;
            return (
              <Badge key={c} variant="light" color={sgOver ? 'red' : 'gray'}>
                {c}: {total}
                {spätgeboren > 0 && (
                  <Tooltip label={`Spätgeborene (max. ${quota})`}>
                    <span style={{ color: sgOver ? 'red' : 'grape', marginLeft: 4 }}>
                      SG:{spätgeboren}/{quota}
                    </span>
                  </Tooltip>
                )}
              </Badge>
            );
          })}
        </Group>
      </Paper>

      {/* Add player row */}
      {!isFinalized && (
        <Paper withBorder p="sm">
          <Group gap="sm" align="flex-end">
            <Select
              label="Spieler hinzufügen"
              placeholder="Name suchen..."
              data={availablePersonOptions}
              value={addPersonId}
              onChange={setAddPersonId}
              searchable
              clearable
              style={{ flex: 1 }}
            />
            <Select
              label="Kategorie"
              data={U_CATEGORIES.map(c => ({ value: c, label: c }))}
              value={addCategory}
              onChange={v => setAddCategory(v ?? 'U17')}
            />
            <Button onClick={handleAddEntry} disabled={!addPersonId}>Hinzufügen</Button>
          </Group>
        </Paper>
      )}

      {/* Drag hint */}
      {!isFinalized && (
        <Text size="xs" c="dimmed">
          Spieler per Drag &amp; Drop zwischen Kategorien verschieben oder das Dropdown "Verschieben" nutzen.
        </Text>
      )}

      {/* Category panels */}
      <Accordion multiple defaultValue={U_CATEGORIES.filter(c => entriesByCategory[c]?.length > 0)}>
        {U_CATEGORIES.map(cat => {
          const entries = entriesByCategory[cat] ?? [];
          const tooFew = entries.length > 0 && entries.length < 12;
          const sgCount = entries.filter(e => e.isSpätgeboren).length;
          const sgQuota = SPÄTGEBOREN_QUOTA[cat];
          const sgOver = sgCount > sgQuota;
          const isDropTarget = dragOverCat === cat;
          const isDraggingFromHere = draggedEntryId !== null &&
            entries.some(e => e.id === draggedEntryId);

          return (
            <div
              key={cat}
              onDragOver={e => handleDragOver(e, cat)}
              onDragLeave={handleDragLeave}
              onDrop={() => handleDrop(cat)}
              style={{
                borderRadius: 8,
                outline: isDropTarget && !isDraggingFromHere
                  ? '2px solid var(--mantine-color-blue-5)'
                  : '2px solid transparent',
                transition: 'outline 120ms ease',
              }}
            >
              <Accordion.Item value={cat}>
                <Accordion.Control>
                  <Group gap="sm">
                    <Text fw={600}>{cat}</Text>
                    <Badge color={tooFew ? 'orange' : 'gray'} variant="light">
                      {entries.length} Spieler
                    </Badge>
                    {sgCount > 0 && (
                      <Tooltip label={`Spätgeborene: max. ${sgQuota} erlaubt`}>
                        <Badge color={sgOver ? 'red' : 'grape'} size="xs">
                          SG: {sgCount}/{sgQuota}
                        </Badge>
                      </Tooltip>
                    )}
                    {tooFew && <Badge color="orange" size="xs">⚠ Wenige Spieler</Badge>}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {entries.length === 0 ? (
                    <Text c="dimmed" size="sm" p="sm">
                      {isDropTarget ? '→ Hier ablegen' : 'Keine Spieler in dieser Kategorie.'}
                    </Text>
                  ) : (
                    <Stack gap={4} p="xs">
                      {entries
                        .sort((a, b) =>
                          `${a.person?.lastName} ${a.person?.firstName}`.localeCompare(
                            `${b.person?.lastName} ${b.person?.firstName}`,
                          ),
                        )
                        .map(entry => {
                          const displayTags = (entry.person?.tags ?? []).filter(
                            t => !U_TAG_PATTERN.test(t.name),
                          );
                          const h2 = isSecondHalf(entry.person?.birthDate);
                          const isDragging = draggedEntryId === entry.id;
                          return (
                            <Paper
                              key={entry.id}
                              withBorder
                              p="xs"
                              draggable={!isFinalized}
                              onDragStart={() => handleDragStart(entry.id)}
                              onDragEnd={handleDragEnd}
                              style={{
                                opacity: isDragging ? 0.4 : 1,
                                cursor: isFinalized ? 'default' : 'grab',
                              }}
                            >
                              <Group justify="space-between" wrap="nowrap" align="flex-start">
                                {/* Left: name + tags + birth date */}
                                <Group gap="xs" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                                  {!isFinalized && (
                                    <IconGripVertical
                                      size={14}
                                      style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0, marginTop: 2 }}
                                    />
                                  )}
                                  <Stack gap={2} style={{ minWidth: 0 }}>
                                    <Group gap="xs" wrap="wrap">
                                      <Text size="sm" fw={500}>
                                        {entry.person?.lastName} {entry.person?.firstName}
                                      </Text>
                                      {displayTags.map(tag => (
                                        <Badge
                                          key={tag.id}
                                          size="xs"
                                          style={{ backgroundColor: tag.color, color: '#fff' }}
                                        >
                                          {tag.name}
                                        </Badge>
                                      ))}
                                    </Group>
                                    <Group gap="xs">
                                      <Text size="xs" c="dimmed">{formatDate(entry.person?.birthDate)}</Text>
                                      {h2 && (
                                        <Tooltip label="2. Halbjahr geboren (Spätgeboren-Kandidat)">
                                          <Badge size="xs" color="yellow" variant="light">H2</Badge>
                                        </Tooltip>
                                      )}
                                    </Group>
                                  </Stack>
                                </Group>

                                {/* Right: SG + actions */}
                                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                                  {isFinalized ? (
                                    entry.isSpätgeboren
                                      ? <Badge size="xs" color="grape">SG</Badge>
                                      : null
                                  ) : (
                                    <>
                                      <Checkbox
                                        size="xs"
                                        checked={entry.isSpätgeboren}
                                        onChange={ev => {
                                          const checked = ev.currentTarget.checked;
                                          handleUpdateEntry(entry.id, { isSpätgeboren: checked });
                                        }}
                                        label="SG"
                                      />
                                      <Select
                                        size="xs"
                                        data={U_CATEGORIES.filter(c => c !== cat).map(c => ({ value: c, label: c }))}
                                        placeholder="Verschieben..."
                                        onChange={v => v && handleUpdateEntry(entry.id, { uCategory: v })}
                                        style={{ width: 120 }}
                                      />
                                      <Button
                                        size="xs"
                                        color="red"
                                        variant="subtle"
                                        onClick={() => setDeleteConfirmEntry(entry)}
                                      >
                                        ×
                                      </Button>
                                    </>
                                  )}
                                </Group>
                              </Group>
                            </Paper>
                          );
                        })}
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </div>
          );
        })}
      </Accordion>
    </Stack>
  );
}

export default TeamDraftDetail;
